import type { Command, CommandHandler, CommandMiddleware } from "../contracts";
import { DuplicateCommandHandlerError, UnknownCommandError } from "./errors";

// The central OUTBOUND dispatcher (architecture.md §4.1). Pure TypeScript — it
// knows nothing about services, navigation, React, or the DOM. Handlers capture
// their own dependencies at registration time, which is what keeps this portable.
export class CommandBus {
    private readonly handlers = new Map<string, CommandHandler>();
    private readonly middleware: CommandMiddleware[] = [];

    // Register the single handler for a command type. Returns an unregister fn.
    register(type: string, handler: CommandHandler): () => void {
        if (this.handlers.has(type)) {
            throw new DuplicateCommandHandlerError(type);
        }
        this.handlers.set(type, handler);
        return () => {
            if (this.handlers.get(type) === handler) {
                this.handlers.delete(type);
            }
        };
    }

    // Add a middleware (logging, confirm-guard, error→toast). Returns a remove fn.
    use(middleware: CommandMiddleware): () => void {
        this.middleware.push(middleware);
        return () => {
            const index = this.middleware.indexOf(middleware);
            if (index >= 0) this.middleware.splice(index, 1);
        };
    }

    has(type: string): boolean {
        return this.handlers.has(type);
    }

    // Dispatch a command through the middleware chain to its handler.
    async dispatch(command: Command): Promise<unknown> {
        const handler = this.handlers.get(command.type);
        if (!handler) {
            throw new UnknownCommandError(command.type);
        }

        let lastIndex = -1;
        const run = (index: number): Promise<unknown> => {
            if (index <= lastIndex) {
                return Promise.reject(new Error("next() called multiple times in a command middleware"));
            }
            lastIndex = index;
            if (index < this.middleware.length) {
                const mw = this.middleware[index];
                return Promise.resolve(mw(command, () => run(index + 1)));
            }
            return Promise.resolve(handler(command));
        };
        return run(0);
    }
}
